import { ElementProps } from '@/components/sections/builder/types';

import {
  ColumnType,
  ElementDataType,
} from '@/components/sections/builder/elements/body/multicolumn/type';
import ImageSetting from '@/components/sections/builder/settings/image-setting';
import { Input } from '@/components/ui/input';
import { useBuilderStore } from '@/stores/builder';
import { ArrowRight, Columns } from 'lucide-react';
import React from 'react';
import { shallow } from 'zustand/shallow';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import useMediaQuery from '@/hooks/useMediaQuery';
import InputLink from '@/components/sections/builder/settings/input-link';
import TextEditorMinimal from '@/components/commons/minimal-tiptap/minimal-editor';
import Link from 'next/link';
import DynamicImage from '@/components/commons/dynamic-image';
import { cn, hexToRgba } from '@/lib/utils';

const defaultData: ColumnType = {
  name: 'Column',
  heading: 'Column',
  description: 'Pair text with an image to focus on your chosen product',
  imageUrl: '',
  link: '',
  linkLabel: '',
  mobileMinWidth: 720,
};

export const ElementContent = ({ element, preview }: ElementProps) => {
  const data = (element.data || defaultData) as ColumnType;

  const parentData = element.parentData as ElementDataType;
  const themeId = element.themeId || '';
  const imgUrl =
    useMediaQuery(data.mobileMinWidth) && data.mobileImg ? data.mobileImg : data.imageUrl;
  const isExternal = data.link?.includes('http');

  return (
    <>
      <div
        className={cn(
          `color-scheme-multi-cols rounded-sm p-4 md:col-span-1 text-${parentData.columnAlignment}`,
          parentData.secondBackground === 'true' && 'bg-secondary backdrop-blur-xl'
        )}
        data-animation='true'
      >
        <Link
          href={
            isExternal
              ? data.link || '#'
              : `/${(data.link || '').replace(/^\/+/, '')}${
                  !isExternal && preview ? `?theme_id=${themeId}&isPreview=true` : ''
                }`
          }
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {imgUrl && (
            <DynamicImage
              src={imgUrl}
              alt='content'
              className={cn(
                'mb-6',
                parentData.columnAlignment === 'center' && 'mx-auto',
                parentData.imageWidth === 'full_width' && 'w-full',
                parentData.imageWidth === 'half_width' && 'w-1/2',
                parentData.imageWidth === 'one_third_width' && 'w-1/3',
                parentData.imageRatio === 'square' && 'aspect-[1/1] object-cover',
                parentData.imageRatio === 'potrait' && 'aspect-[9/16] object-cover',
                parentData.imageRatio === 'circle' && 'aspect-[1/1] rounded-full object-cover'
              )}
              width={100}
              height={100}
              sizes='100vw'
            />
          )}
          <p
            className='mb-3 font-semibold tracking-tight '
            dangerouslySetInnerHTML={{ __html: data.heading || '' }}
          ></p>
          <p dangerouslySetInnerHTML={{ __html: data.description || '' }}></p>
        </Link>
        {data.linkLabel && (
          <div className='pt-4'>
            <Link
              href={`${data.link}${
                !isExternal && preview ? `?theme_id=${themeId}&isPreview=true` : ''
              }`}
            >
              <h2
                className={cn(
                  `mb-2 flex items-center gap-x-2 tracking-tight ${
                    parentData.colorScheme?.text ?? 'text-gray-900'
                  }`
                )}
              >
                {data?.linkLabel}
                <ArrowRight className='w-4 h-4' />
              </h2>
            </Link>
          </div>
        )}
      </div>

      <style jsx>{`
        .bg-secondary {
          background-color: ${hexToRgba(
            parentData.secondaryBgColor?.color,
            (parentData.secondaryBgColor?.opacity ?? 0) / 100 || 0.2
          ) || 'rgba(55, 65, 81, 0.2)'};
        }
      `}</style>
    </>
  );
};

export const ElementPreviewIcon = Columns;

export function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setChildData: state.setChildData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ColumnType;

  return (
    <div>
      <ImageSetting
        value={data?.imageUrl}
        onChange={(v) =>
          builder.setChildData(element.parentId as string, element.id, {
            imageUrl: v,
          })
        }
      />
      <div className='mt-4 mb-2'>Mobile image (optional)</div>
      <ImageSetting
        value={data?.mobileImg}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            mobileImg: val,
          });
        }}
      />
      <InputSlider
        label='Device under this size use mobile image'
        value={data.mobileMinWidth || 0}
        max={1440}
        min={0}
        rightContent={<div>px</div>}
        onChange={(val) =>
          builder.setChildData(element.parentId as string, element.id, {
            mobileMinWidth: val,
          })
        }
      />
      <div className='my-4'>
        <h3 className='mb-1 text-sm'>Heading</h3>
        <TextEditorMinimal
          value={data.heading as string}
          onChange={(val) => {
            builder.setChildData(element.parentId as string, element.id, {
              heading: val,
            });
          }}
          output='html'
          editorProps={{
            attributes: {
              class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
            },
          }}
          editable
        />
      </div>
      <div className='mb-1 text-sm'>Description</div>
      <TextEditorMinimal
        value={data.description as string}
        onChange={(val) => {
          builder.setChildData(element.parentId as string, element.id, {
            description: val,
          });
        }}
        output='html'
        editorProps={{
          attributes: {
            class: 'prose lg:prose-lg prose-blue dark:prose-invert p-2',
          },
        }}
        editable
      />
      <div className='mb-1 text-sm md:mt-4'>Link label</div>
      <Input
        onChange={(e) =>
          builder.setChildData(element.parentId as string, element.id, {
            linkLabel: e.target.value,
          })
        }
        value={data.linkLabel}
        name='linkLabel'
        type='text'
        size={100}
      />
      <div className='mb-1 text-sm md:mt-4'>Button link</div>
      <InputLink
        name='url_path'
        placeholder='Search or enter a link'
        onChange={(value) => {
          builder.setChildData(element.parentId as string, element.id, {
            link: value,
          });
        }}
        value={data.link as string}
      />
    </div>
  );
}

const Column = {
  type: 'column',
  group: 'multicolumn',
  defaultData,
  Element: ElementContent,
  Setting: ElementSetting,
  PreviewIcon: ElementPreviewIcon,
  Icon: ElementPreviewIcon,
  childs: [],
};

export default Column;
